/**
 * REST API data provider.
 *
 * Fetches catalog and product data from the Mirrably backend.
 * All requests include an `X-API-Key` header when an API key is configured.
 */

import type { IDataProvider, Product } from './provider.js';
import { JsonRestClient } from './rest-client.js';
import type { RestClientConfig } from './rest-client.js';

/** Configuration for the REST provider. */
export type RestProviderConfig = RestClientConfig;

export class RestDataProvider<P = Product> implements IDataProvider<P> {
  private readonly client: JsonRestClient;

  constructor(config: RestProviderConfig) {
    this.client = new JsonRestClient(config);
  }

  // ---------------------------------------------------------------------------
  // IDataProvider implementation
  // ---------------------------------------------------------------------------

  async getCatalog(storeId: string): Promise<P[]> {
    const data = await this.client.get<{ products: P[] }>(
      `/stores/${encodeURIComponent(storeId)}/catalog`,
    );
    return data.products;
  }

  async getProduct(productId: string): Promise<P> {
    return this.client.get<P>(`/products/${encodeURIComponent(productId)}`);
  }

  async getModelUrl(productId: string): Promise<string> {
    const data = await this.client.get<{ modelUrl: string }>(
      `/products/${encodeURIComponent(productId)}/model-url`,
    );
    return data.modelUrl;
  }
}
